import { useState, useRef, useCallback } from 'react';
import ProfilePhotoCropDialog from '../components/profile/ProfilePhotoCropDialog';

/**
 * @param {{ profile: object | undefined, onUploaded?: () => void, enqueueSnackbar: (msg: string, opts?: object) => void }} options
 */
export function useProfilePhotoCropFlow({ profile, onUploaded, enqueueSnackbar }) {
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const fileInputRef = useRef(null);

  const closeCrop = useCallback(() => {
    setImageSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setCropOpen(false);
  }, []);

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (event.target) event.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please select an image file', { variant: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('Image size must be less than 5MB', { variant: 'error' });
        return;
      }
      setImageSrc((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      setCropOpen(true);
    },
    [enqueueSnackbar]
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const cropDialog = (
    <ProfilePhotoCropDialog
      open={cropOpen && !!imageSrc}
      imageSrc={imageSrc}
      profile={profile}
      onClose={closeCrop}
      onUploaded={onUploaded}
    />
  );

  return { fileInputRef, handleFileChange, openFilePicker, cropDialog };
}

export default useProfilePhotoCropFlow;
